import { useEffect, useRef, useState } from 'react';
import { useStore, type Stage } from './state/store';
import { useDebrief } from './state/debrief';
import type { SimClient } from './sim/client';
import { LiveStage } from './ui/stages/LiveStage';
import { ScenarioStage } from './ui/stages/ScenarioStage';
import { PlanningStage } from './ui/stages/PlanningStage';
import { DebriefStage } from './ui/stages/DebriefStage';
import { SharedFightLanding } from './ui/stages/SharedFightLanding';
import { MapProof } from './ui/MapProof';
import { DesignRecord } from './ui/design/DesignRecord';
import { Wordmark } from './ui/Wordmark';
import { DEBUG } from './debug';

/**
 * `?proof` still opens the checkpoint-1 map and overlay proof; the stages own the app otherwise.
 *
 * It is a development harness, so it only answers in development or under `?debug` (src/debug.ts) -
 * a deployed build no longer ships a second, older app behind a query string.
 */
const SHOW_PROOF = DEBUG && typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('proof');

/** `?design` opens the design record directly, for sharing the link without walking the stages. */
const SHOW_DESIGN = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('design');

/**
 * Pitch 8, "shareable fights": `?fight=<record>` opens the landing card instead of the ordinary stages.
 * Read once at module load, like `SHOW_DESIGN` - the landing card itself strips the query string once it
 * hands off to "watch the replay" or "fight it yourself" (`src/ui/stages/SharedFightLanding.tsx`), so this
 * constant only ever matters for the page load that arrived with the link.
 */
const FIGHT_TOKEN = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('fight') : null;

export function StagePlaceholder() {
  const stage = useStore((s) => s.stage);
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center">
        <Wordmark size="page" />
        <div className="mt-2 text-fog">stage: {stage}</div>
      </div>
    </div>
  );
}

/**
 * Catches the worker's one `ended` message for the debrief.
 *
 * The live store registers its own `onEnded` handler (it only needs the final snapshot for the end
 * banner); `SimClient` keeps a list of handlers, so this second one runs alongside it and keeps the
 * whole payload — the full event log, the ground-truth frames and the order log — in the debrief store
 * before switching the stage. Registered once per `SimClient` instance, which is created by
 * `quickStart`/`executePlan` just before the stage becomes `live`.
 */
function useEndedCapture(): void {
  const sim = useStore((s) => s.sim);
  const wired = useRef<SimClient | null>(null);

  useEffect(() => {
    if (!sim || wired.current === sim) return;
    wired.current = sim;
    useDebrief.getState().reset();
    sim.onEnded((snap, events, truth, orderLog, ruleset) => {
      useDebrief.getState().capture({ snap, events, truth, orderLog, ruleset });
      useStore.getState().setStage('debrief');
    });
  }, [sim]);
}

/**
 * The browser tab says which stage you are on.
 *
 * Four stages live at one URL, so the tab title is the only thing that tells a reviewer with six tabs
 * open which one holds the fight. Stage 1 has two faces — the area-of-operations cards and the OPORD
 * (operation order) — and `planDeadline` is what separates reading the order from planning against the
 * clock, the same test `App` uses to pick the component.
 */
function useStageTitle(stage: Stage, planning: boolean): void {
  useEffect(() => {
    const suffix =
      stage === 'scenario' ? 'areas of operations'
        : stage === 'planning' ? (planning ? 'planning' : 'OPORD')
          : stage === 'live' ? 'live'
            : stage === 'debrief' ? 'debrief'
              : 'design record';
    document.title = `WINDTUNNEL — ${suffix}`;
  }, [stage, planning]);
}

export function App() {
  const stage = useStore((s) => s.stage);
  // Stage 1 ends with the OPORD (operation order), which is read on the chosen area of operations: the
  // store is already in 'planning' by then (`chooseAo` fits the scenario but starts no simulation), and
  // "Begin planning" starts the clock, which is what puts the plan rail and the map on screen.
  const planning = useStore((s) => s.planDeadline !== null);
  const previousStage = useStore((s) => s.previousStage);
  const setStage = useStore((s) => s.setStage);
  // The landing card resolves itself (watch the replay / fight it yourself) by changing `stage`; this
  // flag just stops it from reappearing if `?fight=` is somehow still in the URL when that happens.
  const [fightResolved, setFightResolved] = useState(false);
  useEndedCapture();
  useStageTitle(stage, planning);
  if (SHOW_PROOF) return <MapProof />;
  if (FIGHT_TOKEN && !fightResolved) {
    return <SharedFightLanding token={FIGHT_TOKEN} onResolved={() => setFightResolved(true)} />;
  }
  // A shared `/?design` link opens the record straight away, with no stage machinery behind it; its
  // "Back" chip drops the query string and reloads onto the ordinary scenario stage.
  if (SHOW_DESIGN && stage !== 'design') {
    return (
      <DesignRecord
        returnTo="scenario"
        onBack={() => { window.location.href = window.location.pathname; }}
      />
    );
  }
  switch (stage) {
    case 'scenario':
      return <ScenarioStage />;
    case 'planning':
      return planning ? <PlanningStage /> : <ScenarioStage />;
    case 'live':
      return <LiveStage />;
    case 'debrief':
      return <DebriefStage />;
    case 'design':
      return <DesignRecord returnTo={previousStage} onBack={() => setStage(previousStage)} />;
    default:
      return <StagePlaceholder />;
  }
}
